/**
 * SupplierSignaturePage.jsx
 * ─────────────────────────
 * Screen 6 — Driver signature pad.
 * Route: /supplier/disposal/:projectId/bol/:bolId/sign
 * All data from location.state (forwarded from BOL Preview).
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../shared/context/UserContext';
import { useDisposalOfflineQueue } from '../../hooks/useDisposalOfflineQueue';
import SupplierLayout from '../../layouts/SupplierLayout';
import './SupplierSignaturePage.css';

export default function SupplierSignaturePage() {
  const { projectId, bolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { callerEmail } = useUser();
  const { queueBolClose, queueBolCloseAmended, drainDisposalQueue, getDisposalQueueStatus } = useDisposalOfflineQueue(callerEmail);

  const { bol, project, loadedTools, isAmendedBol, sessionKey: stateSessionKey } = location.state || {};

  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [driverName, setDriverName] = useState('');
  const [closing, setClosing] = useState(false);

  const bolNumber = bol?.bol_number || bolId?.substring(0, 12) || '—';

  const totalWeight = useMemo(() => {
    if (!loadedTools) return 0;
    return loadedTools.reduce((sum, t) => sum + (t.estimated_weight || t.weight_lbs || 0), 0);
  }, [loadedTools]);

  const toolCount = loadedTools?.length || 0;

  // ── Canvas setup ───────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Size canvas to actual pixel dimensions
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
  }, []);

  const getCtx = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    const isOutdoor = localStorage.getItem('tmg_outdoor') === '1';
    ctx.strokeStyle = isOutdoor ? '#ffffff' : '#1a1f2e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    return ctx;
  }, []);

  function getPos(e) {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  }

  function handlePointerDown(e) {
    e.preventDefault();
    drawingRef.current = true;
    setHasSignature(true);
    const ctx = getCtx();
    if (!ctx) return;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  }

  function handlePointerMove(e) {
    e.preventDefault();
    if (!drawingRef.current) return;
    const ctx = getCtx();
    if (!ctx) return;
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  }

  function handlePointerUp(e) {
    e.preventDefault();
    drawingRef.current = false;
  }

  function handleClear() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  }

  // ── Close shipment ─────────────────────────────────────────────────
  async function handleClose() {
    if (!hasSignature || !driverName.trim()) return;
    setClosing(true);

    try {
      // Capture signature as data URL
      // TODO: upload to blob storage post-MVP — storing as base64 for now
      const signatureImageUrl = canvasRef.current?.toDataURL('image/png') || '';

      if (isAmendedBol) {
        await queueBolCloseAmended({
          bol_id: bolId,
          project_id: projectId,
          amended_bol_number: bol.bol_number,
          closed_by: callerEmail,
          signature_image_url: signatureImageUrl,
          driver_name_printed: driverName.trim(),
          estimated_weight_lbs: totalWeight,
        });
      } else {
        await queueBolClose({
          bol_id: bolId,
          project_id: projectId,
          closed_by: callerEmail,
          signature_image_url: signatureImageUrl,
          driver_name_printed: driverName.trim(),
          estimated_weight_lbs: totalWeight,
        });
      }

      // Clear session (skip if sessionKey is null — reopen flow has no session)
      if (stateSessionKey !== null) {
        const cleanupKey = stateSessionKey || `tmg_loading_session_${projectId}`;
        try { sessionStorage.removeItem(cleanupKey); } catch { /* ignore */ }
      }

      // TRIGGER 2: Explicit drain — await so all items including CLOSE are processed
      await drainDisposalQueue(callerEmail);

      // Verify queue is empty — retry once if items remain
      const status = await getDisposalQueueStatus();
      if (status.pending > 0 || status.failed > 0) {
        await drainDisposalQueue(callerEmail);
      }

      // Navigate to project selector
      navigate('/supplier/disposal', { replace: true });
    } catch (err) {
      console.error('Close shipment failed:', err);
      setClosing(false);
    }
  }

  const canSubmit = hasSignature && driverName.trim() !== '' && !closing;

  // Missing state
  if (!bol || !project) {
    return (
      <SupplierLayout>
        <div className="sig-header">
          <button className="sig-back" onClick={() => navigate('/supplier/disposal')}>&larr;</button>
          <span className="sig-header-title">Driver Signature</span>
          <span className="sig-header-logo">TMG</span>
        </div>
        <div className="sig-state">Session data not found. Please go back and try again.</div>
        <div style={{ textAlign: 'center', padding: 16 }}>
          <button className="sig-btn-ghost" onClick={() => navigate('/supplier/disposal')}>
            &larr; Back to Projects
          </button>
        </div>
      </SupplierLayout>
    );
  }

  return (
    <SupplierLayout>
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="sig-header">
        <button className="sig-back"
          onClick={() => navigate(`/supplier/disposal/${projectId}/bol/${bolId}/preview`, { state: location.state })}>
          &larr;
        </button>
        <span className="sig-header-title">Driver Signature</span>
        <span className="sig-header-logo">TMG</span>
      </div>

      {/* ── BOL summary card ──────────────────────────────────────────── */}
      <div className="sig-summary-card">
        <div className="sig-summary-bol">{isAmendedBol ? `${bolNumber}-A` : bolNumber}</div>
        <div className="sig-summary-row">
          <span>{project.project_number}</span>
          <span>{project.oem}</span>
        </div>
        <div className="sig-summary-row">
          <span>{toolCount} tool{toolCount !== 1 ? 's' : ''}</span>
          <span>{totalWeight.toLocaleString()} lbs est.</span>
        </div>
      </div>

      {/* ── Signature section ─────────────────────────────────────────── */}
      <div className="sig-section">
        <div className="sig-label">Driver Signature</div>
        <div className="sig-instruction">Sign below using your finger</div>
        <canvas
          ref={canvasRef}
          className="sig-canvas"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onTouchStart={handlePointerDown}
          onTouchMove={handlePointerMove}
          onTouchEnd={handlePointerUp}
        />
        <button className="sig-clear-btn" onClick={handleClear}>Clear</button>
      </div>

      {/* ── Driver name input ─────────────────────────────────────────── */}
      <div className="sig-name-section">
        <label className="sig-label">Driver Name (print)</label>
        <input
          type="text"
          className="sig-name-input"
          value={driverName}
          onChange={e => setDriverName(e.target.value)}
          placeholder="Full name"
        />
      </div>

      {/* ── Action button ─────────────────────────────────────────────── */}
      <div className="sig-actions">
        <button
          className={`sig-submit-btn ${canSubmit ? '' : 'disabled'}`}
          onClick={handleClose}
          disabled={!canSubmit}
        >
          {closing ? 'Closing shipment...' : 'Accept & Close Shipment'}
        </button>
      </div>
    </SupplierLayout>
  );
}
